/**
 * 管理后台 API 客户端，对应服务端 /api/admin/*。
 */

export interface PageResult<T> {
  items: T[] | null
  total: number
  limit: number
  offset: number
}

export interface RoomRow {
  id: number
  name: string
  group: string
  maxUsers: number
  open: boolean
  hasPass: boolean
  notice: string
  master: string
  /** 权限位数值（线上格式）；字母串仅用于老库存储 */
  defaultProp: number
  serverId: number
  online: number
}

export interface UserRow {
  account: string
  nick: string
  grade: string
  userType: string
  email: string
  locked: boolean
  regTime: number
  lastTime: number
}

export interface GradeRow {
  name: string
  /** 权限位数值 */
  prop: number
  level: number
}

export interface LogRow {
  kind: string
  account: string
  roomId?: number
  action?: string
  ip?: string
  time: number
  duration?: number
}

export interface RoomStat {
  roomId: number
  name: string
  userCount: number
  flags: number
}

export interface OnlineInfo {
  rooms: RoomStat[] | null
  totalUsers: number
  version: string
}

export interface RecordRow {
  recordId: string
  roomId: number
  title: string
  fileName: string
  size: number
  beginTime: number
  duration: number
  owner: string
  public: boolean
  url: string
}

/** 带错误码的 API 异常。 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

import { apiURL } from './endpoint'

/** 统一的请求封装：自动带令牌、统一错误处理。 */
async function req<T>(
  token: string,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
  }
  if (body !== undefined) init.body = JSON.stringify(body)

  const resp = await fetch(apiURL(path), init)
  const text = await resp.text()
  const data = text ? JSON.parse(text) : null

  if (!resp.ok) {
    throw new ApiError(resp.status, data?.code ?? 'INTERNAL', data?.msg ?? '请求失败')
  }
  return data as T
}

/** 拼查询串，跳过空值。 */
function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== 0) p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const adminApi = {
  // ---- 房间 ----
  listRooms: (t: string, q = '', limit = 50, offset = 0) =>
    req<PageResult<RoomRow>>(t, 'GET', `/api/admin/rooms${qs({ q, limit, offset })}`),

  getRoom: (t: string, id: number) => req<RoomRow>(t, 'GET', `/api/admin/rooms/${id}`),

  createRoom: (t: string, body: Partial<RoomRow> & { id: number; password?: string }) =>
    req<RoomRow>(t, 'POST', '/api/admin/rooms', body),

  updateRoom: (t: string, id: number, body: Record<string, unknown>) =>
    req<{ status: string }>(t, 'PATCH', `/api/admin/rooms/${id}`, body),

  deleteRoom: (t: string, id: number) =>
    req<{ status: string }>(t, 'DELETE', `/api/admin/rooms/${id}`),

  // ---- 主持人名单 ----
  listOps: (t: string, id: number) =>
    req<{ ops: string[] }>(t, 'GET', `/api/admin/rooms/${id}/ops`),

  addOp: (t: string, id: number, account: string) =>
    req<{ status: string }>(t, 'POST', `/api/admin/rooms/${id}/ops`, { account }),

  removeOp: (t: string, id: number, account: string) =>
    req<{ status: string }>(t, 'DELETE', `/api/admin/rooms/${id}/ops/${encodeURIComponent(account)}`),

  // ---- 用户 ----
  listUsers: (t: string, q = '', limit = 50, offset = 0) =>
    req<PageResult<UserRow>>(t, 'GET', `/api/admin/users${qs({ q, limit, offset })}`),

  createUser: (t: string, body: Partial<UserRow> & { account: string; password: string }) =>
    req<UserRow>(t, 'POST', '/api/admin/users', body),

  updateUser: (t: string, account: string, body: Record<string, unknown>) =>
    req<{ status: string }>(t, 'PATCH', `/api/admin/users/${encodeURIComponent(account)}`, body),

  deleteUser: (t: string, account: string) =>
    req<{ status: string }>(t, 'DELETE', `/api/admin/users/${encodeURIComponent(account)}`),

  // ---- 等级 ----
  listGrades: (t: string) => req<{ grades: GradeRow[] }>(t, 'GET', '/api/admin/grades'),

  upsertGrade: (t: string, g: GradeRow) =>
    req<{ status: string }>(t, 'PUT', '/api/admin/grades', g),

  // ---- 日志与监控 ----
  queryLogs: (
    t: string,
    p: { kind?: string; account?: string; roomId?: number; limit?: number; offset?: number },
  ) => req<PageResult<LogRow>>(t, 'GET', `/api/admin/logs${qs(p as Record<string, string | number>)}`),

  online: (t: string) => req<OnlineInfo>(t, 'GET', '/api/admin/online'),

  // ---- 录像 ----
  listRecords: (t: string, roomId: number, limit = 50, offset = 0) =>
    req<{ records: RecordRow[] | null }>(t, 'GET', `/api/rooms/${roomId}/records${qs({ limit, offset })}`),

  deleteRecord: (t: string, recordId: string) =>
    req<{ status: string }>(t, 'DELETE', `/api/records/${encodeURIComponent(recordId)}`),
}

/** 把字节数格式化成可读大小。 */
export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

/** 把秒级时间戳格式化成 YYYY-MM-DD HH:MM。 */
export function formatTime(sec: number): string {
  if (!sec) return '—'
  const d = new Date(sec * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** 把秒数格式化成时长。 */
export function formatDuration(sec: number): string {
  if (!sec) return '—'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  if (h > 0) return `${h}小时${m}分`
  if (m > 0) return `${m}分${s}秒`
  return `${s}秒`
}
